import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import Loader from 'components/Loader';
import pluralize from 'pluralize';
import React, { FunctionComponent, useEffect, useState } from 'react';

import { ManifestErrors } from './ManifestFeedback';
import { CaseOrganizationExecutionMode, caseOrganizationHandlingLabels } from './caseOrganizationPlan';
import { ManifestDestination } from './loadManifestDestination';
import { ManifestWorkbookContextResult } from './manifestWorkbookContext';
import { useManifestTemplateDownload } from './useManifestTemplateDownload';

const handlingGuidance: Record<CaseOrganizationExecutionMode, string> = {
  routine: 'Other slides stay in their current cases.',
  selectedCaseOverride: 'Listed cases keep only the slides shown here. Other cases stay.',
  wholeStudyReplacement: 'Cases not listed here are deleted from this study.',
};

const replacementWarning =
  'Uploading replaces the unsaved case assignment. Saved cases change only after you review and apply.';

interface CaseManifestFileMenuProps {
  canDownload: boolean;
  canUpload: boolean;
  caseCount: number;
  executionMode: CaseOrganizationExecutionMode | null;
  exporting: boolean;
  onDownloadPlan: () => void;
  onUploadClick: () => void;
  closeMenuRef?: React.MutableRefObject<(() => void) | null>;
  hasCurrentPlan?: boolean;
  resolving: boolean;
  slideCount: number;
}

export const CaseManifestFileMenu: FunctionComponent<CaseManifestFileMenuProps> = ({
  canDownload,
  canUpload,
  caseCount,
  executionMode,
  exporting,
  closeMenuRef,
  hasCurrentPlan = true,
  onDownloadPlan,
  onUploadClick,
  resolving,
  slideCount,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const downloadTemplate = useManifestTemplateDownload();
  const closeMenu = () => setAnchorEl(null);
  if (closeMenuRef) closeMenuRef.current = closeMenu;
  const downloadDisabled = !canDownload || exporting;

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        startIcon={
          downloadTemplate.isLoading ? <CircularProgress size={18} color="inherit" /> : <DescriptionOutlinedIcon />
        }
        aria-haspopup="menu"
        aria-expanded={anchorEl ? 'true' : undefined}
        aria-controls={anchorEl ? 'slide-manifest-file-menu' : undefined}
        aria-busy={resolving || exporting || downloadTemplate.isLoading}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={hasCurrentPlan ? { mr: 1 } : undefined}
      >
        Slide manifest file
      </Button>
      <Menu
        id="slide-manifest-file-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        MenuListProps={{ 'aria-label': 'Slide manifest file' }}
      >
        {hasCurrentPlan && (
          <ListSubheader
            component="div"
            sx={{ whiteSpace: 'normal', lineHeight: 1.4, py: 1, bgcolor: 'transparent', maxWidth: 360 }}
          >
            <Typography variant="caption" color="text.secondary" component="p">
              {`Current case assignments: ${pluralize('slide', slideCount, true)} in ${pluralize(
                'case',
                caseCount,
                true
              )}.`}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p">
              {replacementWarning}
              {executionMode ? ` ${handlingGuidance[executionMode]}` : ''}
            </Typography>
          </ListSubheader>
        )}
        {hasCurrentPlan && (
          <MenuItem
            disabled={downloadDisabled}
            onClick={() => {
              closeMenu();
              onDownloadPlan();
            }}
          >
            Download current case assignments
          </MenuItem>
        )}
        <MenuItem
          disabled={downloadTemplate.isLoading}
          onClick={() => {
            closeMenu();
            downloadTemplate.download();
          }}
        >
          Download empty case assignment spreadsheet
        </MenuItem>
        <MenuItem
          disabled={!canUpload || resolving || exporting}
          onClick={() => {
            closeMenu();
            onUploadClick();
          }}
        >
          Upload Excel file
        </MenuItem>
      </Menu>
    </>
  );
};

interface CaseManifestFileStatusProps {
  canMatchContext: boolean;
  contextFeedback?: Extract<ManifestWorkbookContextResult, { status: 'valid' }>;
  destination?: ManifestDestination;
  destinationError?: string;
  disabled: boolean;
  downloadError: string | null;
  errors: string[];
  executionMode: CaseOrganizationExecutionMode | null;
  exporting: boolean;
  onConfirmOverwrite: () => void;
  onDismiss: () => void;
  onDismissDownloadError: () => void;
  onKeepContext: () => void;
  onMatchContext: () => void;
  onOverwriteAccepted: (accepted: boolean) => void;
  onRetryDestination?: () => void;
  overwriteAccepted: boolean;
  pendingOverwrite: boolean;
  resolving: boolean;
}

export const CaseManifestFileStatus: FunctionComponent<CaseManifestFileStatusProps> = ({
  canMatchContext,
  contextFeedback,
  destination,
  destinationError,
  disabled,
  downloadError,
  errors,
  executionMode,
  exporting,
  onConfirmOverwrite,
  onDismiss,
  onDismissDownloadError,
  onKeepContext,
  onMatchContext,
  onOverwriteAccepted,
  onRetryDestination,
  overwriteAccepted,
  pendingOverwrite,
  resolving,
}) => {
  const hasPendingDecision = pendingOverwrite || Boolean(contextFeedback);
  useEffect(() => {
    if (!hasPendingDecision) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      onDismiss();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [hasPendingDecision, onDismiss]);

  if (!downloadError && !exporting && !pendingOverwrite && !contextFeedback && errors.length === 0 && !resolving) {
    return null;
  }

  return (
    <Box data-testid="case-manifest-file-status" sx={{ width: '100%', mb: 1.5 }}>
      {downloadError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={onDismissDownloadError} closeText="Dismiss download error">
          {downloadError}
        </Alert>
      )}
      {exporting && (
        <Box sx={{ mb: 1 }}>
          <Loader
            inline
            message="Preparing the case assignment download…"
            ariaLabel="Preparing case assignment download"
          />
        </Box>
      )}
      {pendingOverwrite && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2">
            The case assignment changed after download. Uploading this file will replace those newer edits.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox checked={overwriteAccepted} onChange={(_event, checked) => onOverwriteAccepted(checked)} />
            }
            label="Replace newer table edits with the uploaded file"
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button color="inherit" onClick={onDismiss} disabled={resolving}>
              Keep current case assignments
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={onConfirmOverwrite}
              disabled={disabled || resolving || !overwriteAccepted}
            >
              Replace current case assignments
            </Button>
          </Box>
        </Alert>
      )}
      {contextFeedback && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {!canMatchContext && (
            <Typography variant="body2">The case manifest file refers to a different study or lab.</Typography>
          )}
          <Typography variant="body2">
            {contextFeedback.context.handling === 'wholeStudyReplacement'
              ? 'This file deletes cases that are not listed.'
              : contextFeedback.context.handling === 'selectedCaseOverride'
              ? 'This file replaces the slides in the listed cases. Other cases stay.'
              : 'This file adds and edits listed cases. Other cases stay.'}
          </Typography>
          <Typography variant="body2">
            {`The assignment on screen uses ${
              executionMode ? caseOrganizationHandlingLabels[executionMode] : 'no selected operation'
            }.`}
          </Typography>
          {destination && (
            <Typography variant="body2">
              Destination: {destination.studyName} ({destination.labName})
            </Typography>
          )}
          {destinationError && <Typography variant="body2">{destinationError}</Typography>}
          <Button color="inherit" onClick={onKeepContext} disabled={disabled || resolving}>
            {executionMode ? `Keep ${caseOrganizationHandlingLabels[executionMode]}` : 'Keep the assignment on screen'}
          </Button>
          {canMatchContext && (
            <Button color="inherit" onClick={onMatchContext} disabled={disabled || resolving}>
              {contextFeedback.context.handling === 'wholeStudyReplacement'
                ? 'Delete cases that are not listed'
                : `Use ${caseOrganizationHandlingLabels[contextFeedback.context.handling]}`}
            </Button>
          )}
          {destinationError && onRetryDestination && (
            <Button color="inherit" onClick={onRetryDestination} disabled={disabled || resolving}>
              Retry destination lookup
            </Button>
          )}
        </Alert>
      )}
      {errors.length > 0 && (
        <Box role="alert" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <ManifestErrors messages={errors} />
          </Box>
          <Tooltip title="Dismiss import error">
            <IconButton color="inherit" size="small" aria-label="Dismiss import error" onClick={onDismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {resolving && (
        <Box aria-live="polite">
          <Box sx={{ mb: 1 }}>
            <Loader inline message="Validating manifest..." />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Editing the case assignment is paused until validation finishes.
            </Typography>
          </Box>
          <Button color="inherit" onClick={onDismiss}>
            Keep current case assignments
          </Button>
        </Box>
      )}
    </Box>
  );
};
